// WritingSection.js - Updated with API integration for first attempt grading
import React, { useState, useEffect } from 'react';
import { CheckCircle, FileText, User, BookOpen, Loader2 } from 'lucide-react';
import AITeacher from './common/AITeacher';
import AICompanion from './common/AICompanion';
import WritingGuidelines from './writing/WritingGuidelines';
import WritingArea from './writing/WritingArea';
import WritingFeedback from './writing/WritingFeedback';
import SelfAssessmentModal from './writing/SelfAssessmentModal';
import CVForm from './writing/CVForm';
import InstructionForSinglePart from '../InstructionForSinglePart';
import QRModal from './modals/QRModal';
import { tokenManager } from '../../routers/api';
import axios from "../../routers/axios";
import useTimer from "../../hooks/useTimer";
import CVFormatSelector from "./writing/CVFormatSelector";
import EndCVSelfCheckModal from "./writing/EndCVSelfCheckModal";




const WritingSection = ({
  currentScenario,
  currentScenarioData,
  writingText,
  setWritingText,
  writingAttempt,
  setWritingAttempt,
  submitted,
  setSubmitted,
  showAIFeedback,
  setShowAIFeedback,
  readingFeedback,
  setCurrentScenario,
  setCurrentStep,
  setAnswers,
  setReadingFeedback,
  scenario1Results,
  setScenario1Results,
  scenario2Results,
  setScenario2Results,
  markScenarioComplete,
  isCurrentScenarioComplete,
  scenario1Completed,
  scenario2Completed,
  allowFinalResults,
  preventAutoFinalResults,
  testId,
  handleEndTest, // ✅ thêm dòng này
  viewMode = false // ✅ thêm
}) => {
  // Flow control states
  const [flowStep, setFlowStep] = useState('writing'); // writing | self-assessment | ai-feedback | writing-v2 | survey | completed
  const [showSelfAssessment, setShowSelfAssessment] = useState(false);
  // 🧩 Assigned exercises (from backend)
  const [assignedExercises, setAssignedExercises] = useState([]);
  const [loadingExercises, setLoadingExercises] = useState(false);

  const [selfAssessmentData, setSelfAssessmentData] = useState({});
  const [writingMode, setWritingMode] = useState(null);
  const [cvData, setCvData] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [writingRubric, setWritingRubric] = useState(null);
  // ✅ NEW: Lưu thông tin chi tiết về Writing (guidelines, rubric, sample, instruction, v.v.)
  const [writingDetail, setWritingDetail] = useState(null);
  const [loadingWritingDetail, setLoadingWritingDetail] = useState(false);


  // NEW: Scenario writing tasks
  const [writingTasks, setWritingTasks] = useState([]);
  const [currentWritingTaskIndex, setCurrentWritingTaskIndex] = useState(0);
  const [showWritingContent, setShowWritingContent] = useState(false);
  const [cvFormatSelected, setCvFormatSelected] = useState(null);
  const [cvFormData, setCvFormData] = useState(null);
  const [showEndCVSelfCheck, setShowEndCVSelfCheck] = useState(false);


  useEffect(() => {
    if (!currentScenarioData?.id) return;
    axios
      .get(`/scenarios/tasks/${currentScenarioData.id}?task_type=writing`)
      .then((res) => {
        const sorted = (res.data || []).sort((a, b) => (a.task_order ?? 0) - (b.task_order ?? 0));
        setWritingTasks(sorted);
        setCurrentWritingTaskIndex(0);
        setShowWritingContent(sorted.length === 0); // Nếu không có task => hiển thị phần viết luôn
      })
      .catch((err) => {
        console.error("❌ Error fetching writing tasks:", err);
        setWritingTasks([]);
        setShowWritingContent(true);
      });
  }, [currentScenarioData]);


  const writingDuration = Number(currentScenarioData?.estimated_time_minutes ?? 0);
  const remainingTimeFromDB = Math.floor(writingDuration * 60);

  const { timeRemaining, formatTime } = useTimer(
    writingDuration > 0 ? writingDuration : 0,
    testId,
    remainingTimeFromDB,   // 👈 KHÔNG để null
    viewMode,
    handleEndTest,
    currentScenarioData?.id,
    "writing"
  );

  // ✅ Gửi giá trị ngay lập tức ra ngoài
  useEffect(() => {
    if (typeof window.onTimerUpdate === "function") {
      window.onTimerUpdate(remainingTimeFromDB, formatTime);
    }
  }, [remainingTimeFromDB]);


  // ✅ NEW: Gọi API để lấy dữ liệu Writing Guidelines chi tiết
  useEffect(() => {
    if (!currentScenarioData?.id) return;

    const fetchWritingDetail = async () => {
      try {
        setLoadingWritingDetail(true);
        const res = await axios.get(`/scenarios/writing/${currentScenarioData.id}`);
        console.log("🧠 Writing detail loaded:", res.data);
        setWritingDetail(res.data);
      } catch (err) {
        console.error("❌ Error fetching writing detail:", err);
        setWritingDetail(null);
      } finally {
        setLoadingWritingDetail(false);
      }
    };

    fetchWritingDetail();
  }, [currentScenarioData?.id]);


  // Store first attempt data
  const [firstAttemptText, setFirstAttemptText] = useState('');
  const [firstAttemptResults, setFirstAttemptResults] = useState(null);

  // ✅ NEW: API grading states
  const [isGrading, setIsGrading] = useState(false);
  const [apiGradingResults, setApiGradingResults] = useState(null);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Helper functions
  const getWritingTask = () => {
    if (!currentScenarioData) return 'Writing Task';
    if (currentScenarioData.writingTask?.instruction) return currentScenarioData.writingTask.instruction;
    if (currentScenarioData.writingInstruction?.task) return currentScenarioData.writingInstruction.task;
    if (currentScenarioData.tasks?.writing) return currentScenarioData.tasks.writing;
    return 'Writing Task';
  };

  const getUserId = () => {
    try {
      const u = tokenManager?.getUser?.();
      return u?.id ?? u?.userId ?? u?.user_id ?? null;
    } catch {
      return null;
    }
  };


  // 🧩 Fetch assigned writing exercises from backend
  const fetchAssignedExercises = async () => {
    if (!currentScenarioData?.id) return;
    try {
      setLoadingExercises(true);
      const res = await axios.get(`/writing_exercises/by_scenario/${currentScenarioData.id}`);
      setAssignedExercises(res.data || []);
    } catch (error) {
      console.error("❌ Error fetching assigned writing exercises:", error);
    } finally {
      setLoadingExercises(false);
    }
  };

  const isCVTask = () => {
    if (!currentScenarioData) return false;
    const writingTask = getWritingTask().toLowerCase();
    return writingTask.includes('cv') ||
      writingTask.includes('resume') ||
      currentScenarioData.writingInstruction?.templates ||
      currentScenarioData.writingTask?.templates;
  };
  useEffect(() => {
    if (currentScenarioData?.id) {
      fetchAssignedExercises();
    }
  }, [currentScenarioData?.id]);

  const getWordCount = (text) => {
    return text.trim().length === 0 ? 0 : text.trim().split(/\s+/).filter(Boolean).length;
  };

  const checkPlagiarism = (text) => {
    const sampleEmail = currentScenarioData?.writingInstruction?.sampleEmail ||
      currentScenarioData?.writingTask?.sampleEmail;
    if (!sampleEmail || writingAttempt === 1) return false;
    const sampleWords = sampleEmail.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/);
    const textWords = text.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/);
    let matchCount = 0;
    textWords.forEach(word => { if (sampleWords.includes(word)) matchCount++; });
    return (matchCount / textWords.length) > 0.7;
  };

  // ✅ FIXED: API grading function for proxy setup
  const gradeEssayWithAPI = async (text, question) => {
    try {
      setIsGrading(true);

      // ✅ When using proxy in package.json, call directly without base URL
      const response = await fetch('https://cmssba.metatekvn.com/api/grade-essay', {
        // const response = await fetch('/api/grade-essay', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentAnswer: text,
          question: question
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('API Success Response:', result);

      // ✅ FIX: Check if result has the expected structure
      if (result.error) {
        throw new Error(result.error);
      }

      // ✅ FIX: Validate required fields
      if (!result.grade || !result.level || !result.feedback || !result.overall_comment) {
        console.warn('API Response missing expected fields:', result);
        throw new Error('Invalid API response structure');
      }

      return result;
    } catch (error) {
      console.error('Error grading essay:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });

      // Return fallback result in case of API failure
      return {
        grade: 'B',
        level: 'B2',
        feedback: {
          communicative_goals: 'Unable to assess due to technical issues.',
          socio_cultural_context: 'Unable to assess due to technical issues.',
          position: 'Unable to assess due to technical issues.',
          argumentation: 'Unable to assess due to technical issues.',
          coherence_cohesion: 'Unable to assess due to technical issues.',
          source_processing: 'Unable to assess due to technical issues.',
          lexical_grammar: 'Unable to assess due to technical issues.'
        },
        overall_comment: `Technical error occurred during grading: ${error.message}`,
        error: true,
        errorDetails: error.message
      };
    } finally {
      setIsGrading(false);
    }
  };

  const calculateScore = (text, attempt) => {
    const wordCount = getWordCount(text);
    const isPlagiarized = checkPlagiarism(text);
    let finalScore = 0;
    const hasCompletedReading = readingFeedback && Object.keys(readingFeedback).length > 0;

    if (isPlagiarized) {
      finalScore = 0;
    } else {
      if (wordCount >= 100) finalScore += 4;
      else if (wordCount >= 50) finalScore += 3;
      else if (wordCount >= 20) finalScore += 2;
      else finalScore += 1;

      if (hasCompletedReading) finalScore += 2;
      if (attempt > 1) finalScore += 1;
      finalScore = Math.min(finalScore, 10);
    }

    return {
      score: finalScore,
      wordCount,
      attempt,
      isPlagiarized,
      hasCompletedReading,
      text,
      timestamp: new Date().toISOString()
    };
  };

  const wordCount = getWordCount(writingText);
  const isPlagiarized = checkPlagiarism(writingText);

  const convertCVDataToText = (data) => {
    if (!data) return '';
    let text = '';
    if (data.personalData) {
      text += `${data.personalData.firstName} ${data.personalData.lastName}\n`;
      text += `${data.personalData.email}\n`;
      if (data.personalData.phone) text += `${data.personalData.phone}\n`;
      if (data.personalData.jobTitle) text += `${data.personalData.jobTitle}\n`;
    }
    if (data.professionalSummary) text += `\nSummary: ${data.professionalSummary}\n`;
    if (data.skills) text += `\nSkills: ${data.skills}\n`;
    if (data.education) text += `\nEducation: ${data.education}\n`;
    if (data.employmentHistory) text += `\nExperience: ${data.employmentHistory}\n`;
    if (data.workExperience) text += `\nWork Experience: ${data.workExperience}\n`;
    if (data.certifications) text += `\nCertifications: ${data.certifications}\n`;
    return text;
  };

  // Confirmation dialog component
  const ConfirmationDialog = ({ message, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Submission</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Submit Anyway
          </button>
        </div>
      </div>
    </div>
  );

  // ✅ UPDATED: Submit handler for first attempt with API grading
  const handleFirstAttemptSubmit = async () => {
    if (viewMode) return; // ✅ không cho submit nếu chỉ xem
    if (!writingText.trim()) {
      alert('Please write something before submitting.');
      return;
    }

    if (wordCount < 10) {
      setConfirmAction(() => () => proceedWithFirstSubmission());
      setShowConfirmDialog(true);
      return;
    }

    await proceedWithFirstSubmission();
  };

  // ✅ UPDATED: Process first submission with API grading
  const proceedWithFirstSubmission = async () => {
    console.log('First attempt submission with API grading');

    try {
      // Store first attempt data
      setFirstAttemptText(writingText);
      const basicResults = calculateScore(writingText, 1);
      setFirstAttemptResults(basicResults);

      // ✅ NEW: Call API for detailed grading
      const writingTask = getWritingTask();
      const apiResults = await gradeEssayWithAPI(writingText, writingTask);
      setApiGradingResults(apiResults);

      // 🔗 Lưu bài viết lần 1 về BE
      try {
        const user_id = getUserId();
        const payload = {
          user_id,
          test_id: Number(testId) || currentScenarioData?.test_id,  // ✅ fallback từ API
          scenario_id: currentScenarioData?.id,
          writing_text: writingText,
          score: basicResults?.score || apiGradingResults?.score || 0,
          feedback: apiGradingResults?.overall_comment || '',
          self_assessment: null
        };

        const res = await axios.post("/test_attempts/writing", payload);
        console.log("[✅ Writing] First attempt saved:", res.data);
        setAttemptId(res.data.id); // ✅ lưu lại attempt ID
      } catch (err) {
        console.error("[❌ Writing] Save attempt 1 failed:", err);
      }



      // Check if self-assessment is required
      // const requiresSelfAssessment = currentScenarioData?.writingProcess?.selfAssessmentRubric ||
      //   currentScenarioData?.selfAssessment;

      // if (requiresSelfAssessment) {
      //   setFlowStep('self-assessment');
      //   setShowSelfAssessment(true);
      // } else {
      //   setFlowStep('ai-feedback');
      //   setShowAIFeedback(true);
      // }

      // ✅ Thay bằng:
      setFlowStep('self-assessment');
      setShowSelfAssessment(true);

      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Error during submission:', error);
      alert('An error occurred during submission. Please try again.');
      setShowConfirmDialog(false);
    }
  };

  // ✅ Self-assessment completion
  const handleSelfAssessmentComplete = async (data) => {
    console.log('✅ Self-assessment completed:', data);
    setSelfAssessmentData(data);
    setShowSelfAssessment(false);

    try {
      const payload = {
        user_id: getUserId(),
        test_id: Number(testId),
        scenario_id: currentScenarioData?.id,
        writing_text: writingText || "",
        score: apiGradingResults?.overallScore || 0,
        feedback: JSON.stringify(apiGradingResults?.feedback || ""), // ✅
        self_assessment: data,
      };

      console.log("[SelfAssessment] Payload gửi BE:", payload);
      await axios.post("/test_attempts/writing", payload);
      console.log("✅ Self-assessment saved successfully");
    } catch (error) {
      console.error("❌ Failed to save self-assessment:", error);
    }

    setFlowStep('ai-feedback');
    setShowAIFeedback(true);
  };


  // ✅ AI feedback viewed, move to second attempt
  const handleAIFeedbackNext = () => {
    setShowAIFeedback(false);

    // Nếu là CV → quay về CVForm, KHÔNG sang WritingArea
    const isCV = assignedExercises.some(ex => ex.type?.toLowerCase() === "cv");

    if (isCV) {
      setFlowStep("writing");      // quay về flow CV ban đầu
      setWritingAttempt(1);        // CV không có attempt 2
      return;
    }

    // Các writing bình thường thì tiếp tục writing-v2
    setFlowStep('writing-v2');
    setWritingAttempt(2);
    setWritingText('');
  };


  // ✅ Submit second attempt (NO API call - just basic scoring)
  const handleSecondAttemptSubmit = () => {
    if (viewMode) return; // ✅ không cho submit nếu chỉ xem
    if (!writingText.trim()) {
      alert('Please write something before submitting.');
      return;
    }

    if (wordCount < 10) {
      setConfirmAction(() => () => proceedWithSecondSubmission());
      setShowConfirmDialog(true);
      return;
    }

    proceedWithSecondSubmission();
  };

  // ✅ Process second submission (NO API call)
  const proceedWithSecondSubmission = async () => {
    console.log('Second attempt submission - using basic scoring only');

    const basicResults = calculateScore(writingText, 2);

    try {
      const user_id = getUserId();
      const payload = {
        user_id,
        test_id: Number(testId) || currentScenarioData?.test_id, // ✅ fallback từ API
        scenario_id: currentScenarioData?.id,
        writing_text: writingText,
        score: basicResults?.score || apiGradingResults?.score || 0,
        feedback: apiGradingResults?.overall_comment || '',
        self_assessment: null
      };

      const res = await axios.post("/test_attempts/writing", payload);
      console.log("[✅ Writing] Attempt saved:", res.data);
    } catch (err) {
      console.error("[❌ Writing] Save failed:", err);
    }

    // ✅ Tạo object kết quả đúng cấu trúc để gửi cho markScenarioComplete
    const structuredResults = {
      scenario: currentScenario,
      readingScore:
        currentScenario === 1
          ? (scenario1Results?.readingScore ?? 0)
          : (scenario2Results?.readingScore ?? 0),
      writingScore: basicResults?.score || 0,
      totalScore:
        ((currentScenario === 1
          ? (scenario1Results?.readingScore ?? 0)
          : (scenario2Results?.readingScore ?? 0)) +
          (basicResults?.score || 0)) / 2,
      readingCompleted: true, // ✅ vì writing chỉ chạy sau khi đọc xong
      writingCompleted: true,
      isPlagiarized: false,
      attempt: 2,
      wordCount: basicResults?.wordCount || 0,
    };

    // ✅ Lưu state nội bộ để hiển thị
    if (currentScenario === 1) {
      setScenario1Results?.(structuredResults);
    } else if (currentScenario === 2) {
      setScenario2Results?.(structuredResults);
    }

    setSubmitted(true);

    // ✅ Hiển thị survey như trước
    // ❗ Chỉ show QR khi là scenario cuối cùng
    if (currentScenario === 2) {
      setFlowStep('survey');
      setShowQRModal(true);
    } else {
      // Scenario 1 → tự động qua Scenario 2
      handleMoveToNextScenario();
    }
    setShowConfirmDialog(false);
  };


  // ✅ Move to next scenario
  const handleMoveToNextScenario = async () => {
    if (currentScenario === 1) {
      try {
        const user = tokenManager.getUser();
        if (!user || !testId) {
          console.warn("⚠️ Missing user or testId when moving to next scenario");
          return;
        }

        // 🟢 Gọi BE để tạo bản ghi attempt mới cho Scenario 2
        const nextScenarioId = 2;
        const res = await axios.post(`/test_attempts/start/${user.id}/${testId}/${nextScenarioId}`);
        console.log("✅ Created new attempt for Scenario 2:", res.data);

        // 🔄 Sau khi BE đã tạo, cập nhật FE state
        setCurrentScenario(nextScenarioId);
        setCurrentStep("reading");

        // Reset writing states
        setWritingText("");
        setWritingAttempt(1);
        setSubmitted(false);
        setShowAIFeedback(false);
        setSelfAssessmentData({});
        setFirstAttemptText("");
        setFirstAttemptResults(null);
        setApiGradingResults(null);
        setFlowStep("writing");

      } catch (error) {
        console.error("❌ Failed to create next scenario attempt:", error);
      }
    }
  };


  // ✅ Survey completed
  const handleSurveyComplete = () => {
    setShowQRModal(false);
    setFlowStep('completed');

    // Navigate to dashboard/results
    // if (allowFinalResults && !preventAutoFinalResults) {
    //   setCurrentStep('results');
    // }
  };

  // CV submit handler (unchanged but now with API integration)
  const handleCVSubmit = async (data) => {
    if (viewMode) return;

    console.log("📄 Submitting CV Data:", data);

    try {
      const userId = getUserId();

      // 1️⃣ Convert CV JSON sang text để chấm & lưu
      const textVersion = convertCVDataToText(data);
      setCvData(data);
      setWritingText(textVersion);

      // 2️⃣ LƯU CV VÀO BẢNG cv_submissions
      const cvPayload = {
        user_id: userId,
        test_id: Number(testId),
        scenario_id: currentScenarioData?.id,
        cv_json: data,
        cv_text: textVersion
      };

      console.log("📌 Sending CV JSON to /cv_form/submit:", cvPayload);

      const cvRes = await axios.post("/cv_form/submit", cvPayload);
      console.log("✅ CV Saved:", cvRes.data);

      // 3️⃣ Chấm Writing + lưu attempt như bình thường
      await handleFirstAttemptSubmit();

    } catch (error) {
      console.error("❌ Error submitting CV:", error);
      alert("An error occurred while submitting CV.");
    }
  };

  // ✅ CV Form Complete Handler
  const handleCVFormComplete = async (data) => {
    console.log("📄 CV Form completed:", data);

    setCvFormData(data);
    const userId = getUserId();
    const cvText = convertCVDataToText(data);

    // LƯU CV NGAY LẬP TỨC
    const cvPayload = {
      user_id: userId,
      test_id: Number(testId),
      scenario_id: currentScenarioData?.id,
      cv_json: data,
      cv_text: cvText
    };

    console.log("📌 Saving CV immediately after Finish CV:", cvPayload);
    await axios.post("/cv_form/submit", cvPayload);

    // Sau khi lưu → mở modal self-check
    setShowEndCVSelfCheck(true);
  };

  // ✅ EndCVSelfCheck Complete Handler
  const handleEndCVSelfCheckComplete = async () => {
    console.log("✅ EndCVSelfCheck completed");
    setShowEndCVSelfCheck(false);

    try {
      // 1️⃣ KHÔNG LƯU CV Ở ĐÂY — ĐÃ LƯU Ở BƯỚC FINISH CV

      // 2️⃣ Chuẩn bị dữ liệu viết (dùng lại từ lần lưu CV ban đầu)
      const cvText = convertCVDataToText(cvFormData);
      setWritingText(cvText);
      setCvData(cvFormData);

      // 3️⃣ Submit First Attempt (gọi AI grading + ghi test_attempt)
      await proceedWithFirstSubmission();

    } catch (error) {
      console.error("❌ Error at EndCVSelfCheckComplete:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  // ✅ Show loading overlay during API grading
  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Grading Your Writing</h3>
        <p className="text-gray-600 text-sm">
          AI Teacher is analyzing your work. This may take a few moments...
        </p>
      </div>
    </div>
  );

  // Render second attempt writing area with first attempt reference
  const renderSecondAttemptWriting = () => {
    return (
      <div className="space-y-6">
        {/* Second attempt writing area */}
        <div className="flex gap-6">
          <div className="w-[35%] flex-shrink-0">
            <div className="sticky top-6">
              <div className="bg-white rounded-lg shadow-sm border max-h-[calc(100vh-8rem)] flex flex-col">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="text-lg font-semibold">📚 Writing Guidelines</h3>
                  <p className="text-sm text-gray-600">Reference materials for your second attempt</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <WritingGuidelines
                    currentScenarioData={currentScenarioData}
                    writingAttempt={writingAttempt}
                    isPlagiarized={isPlagiarized}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <WritingArea
              writingText={writingText}
              setWritingText={setWritingText}
              writingAttempt={writingAttempt}
              wordCount={wordCount}
              isPlagiarized={isPlagiarized}
              handleSubmitWriting={handleSecondAttemptSubmit}
              currentScenarioData={writingDetail || currentScenarioData}
              currentScenario={currentScenario}
              testId={testId}
              isSecondAttempt={true}
              firstAttemptText={firstAttemptText}
              firstAttemptResults={firstAttemptResults}
              viewMode={viewMode} // ✅ thêm dòng này
            />
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (!currentScenarioData) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-400 rounded-lg">
        <p className="text-yellow-800">Loading writing section data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Loading overlay during API grading */}
      {isGrading && <LoadingOverlay />}

      {/* AI Teacher */}
      <AITeacher
        type="writing"
        testId={testId}
        scenarioId={currentScenario}
        scenarioData={currentScenarioData}
        videoFileName={currentScenarioData?.instruction_writing?.videoFileName}
      />

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmationDialog
          message={`Your text is quite short (${wordCount} words). Are you sure you want to submit?`}
          onConfirm={() => {
            setShowConfirmDialog(false);
            if (confirmAction) {
              confirmAction();
            }
          }}
          onCancel={() => {
            setShowConfirmDialog(false);
            setConfirmAction(null);
          }}
        />
      )}

      {/* QR Survey Modal */}
      <QRModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        onSurveyComplete={handleSurveyComplete}
        currentScenario={currentScenario}
        onContinueScenario2={handleMoveToNextScenario}   // ✅ dùng đúng hàm đã có sẵn
        onSubmitFinal={() => {
          setShowQRModal(false);

          if (viewMode) {
            console.log("👀 View mode — skipping FinalResultsModal");
            return;
          }

          if (handleEndTest) {
            handleEndTest();
          } else {
            const currentResults =
              currentScenario === 1 ? scenario1Results : scenario2Results;
            if (markScenarioComplete) {
              markScenarioComplete(currentScenario, currentResults);
            }
          }
        }}
      />

      {/* ✅ Nếu có instruction thì hiển thị trước, nếu không thì hiển thị phần viết */}
      {!showWritingContent ? (
        <InstructionForSinglePart
          scenarioId={currentScenarioData?.id}
          instructionType="writing"
          onComplete={() => setShowWritingContent(true)}
          onLoaded={(data) => {
            console.log("🧠 Writing data loaded:", data);
            setWritingRubric(data.rubric_json || null);
          }}
        />

      ) : (
        <>
          {/* 🧩 Assigned Writing Exercises */}
          {loadingExercises ? (
            <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 p-3 rounded">
              Loading writing exercises...
            </div>
          ) : assignedExercises.length > 0 ? (
            <div className="bg-white border rounded-lg shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                📝 Assigned Writing Exercises
              </h3>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 text-gray-600 text-sm p-3 rounded">
              No writing exercises assigned for this scenario.
            </div>
          )}

          {/* ✅ Khi không có instruction, hiển thị như trước */}
          {flowStep === "writing" && (
            <>
              {assignedExercises.some(ex => ex.type?.toLowerCase() === "cv") ? (
                <div className="flex gap-6 mt-6">

                  {/* LEFT: AI Teacher / Video */}
                  <div className="w-1/3 flex-shrink-0">
                    <div className="sticky top-6">
                      <div className="bg-white rounded-lg shadow-sm border p-4">
                        <video
                          src="/AI_Video/test2_scenario1_writing_startcv.mp4"
                          className="w-full rounded"
                          controls
                          preload="metadata"
                        />
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="flex-1">

                    {/* 👇 Nếu CHƯA chọn loại CV → hiển thị bộ chọn CV format */}
                    {cvFormatSelected === null && (
                      <CVFormatSelector
                        onSelect={(format) => {
                          setCvFormatSelected(format);
                        }}
                      />
                    )}

                    {/* ✅ EndCVSelfCheckModal */}
                    {showEndCVSelfCheck && (
                      <EndCVSelfCheckModal
                        isOpen={showEndCVSelfCheck}
                        onClose={() => setShowEndCVSelfCheck(false)}
                        onNextPart={handleEndCVSelfCheckComplete}
                      />
                    )}

                    {/* 👇 Nếu ĐÃ chọn → hiển thị CV Form */}
                    {cvFormatSelected !== null && (() => {

                      // ⭐ Quan trọng: CHỌN ĐÚNG BÀI CV THEO FORMAT
                      const selectedCV = assignedExercises.find(
                        ex =>
                          ex.type?.toLowerCase() === "cv" &&
                          ex.format?.toLowerCase() === cvFormatSelected?.toLowerCase()
                      );

                      if (!selectedCV) return null;

                      return (
                        <CVForm
                          key={selectedCV.id}
                          cvStructure={
                            typeof selectedCV.cv_structure === "string"
                              ? JSON.parse(selectedCV.cv_structure)
                              : selectedCV.cv_structure || []
                          }
                          scenarioData={{
                            ...currentScenarioData,
                            exercise: selectedCV,  // ⭐ Không override format nữa
                            cv_data: currentScenarioData?.cv_data || null
                          }}
                          onComplete={handleCVFormComplete}
                          onBackToFormat={() => setCvFormatSelected(null)}
                        />
                      );

                    })()}
                  </div>

                </div>
              ) : (
                <div className="flex gap-6 mt-6">
                  <div className="w-[35%] flex-shrink-0">
                    <div className="sticky top-6">
                      <div className="bg-white rounded-lg shadow-sm border max-h-[calc(100vh-8rem)] flex flex-col">
                        <div className="p-4 border-b bg-gray-50">
                          <h3 className="text-lg font-semibold">📚 Writing Guidelines</h3>
                          <p className="text-sm text-gray-600">
                            Reference materials for your task
                          </p>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                          {loadingWritingDetail ? (
                            <div className="text-gray-500 text-sm italic">Loading Writing Guidelines...</div>
                          ) : (
                            <WritingGuidelines
                              currentScenarioData={writingDetail || currentScenarioData}
                              writingAttempt={writingAttempt}
                              isPlagiarized={isPlagiarized}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                      <WritingArea
                        writingText={writingText}
                        setWritingText={setWritingText}
                        writingAttempt={writingAttempt}
                        wordCount={wordCount}
                        isPlagiarized={isPlagiarized}
                        handleSubmitWriting={handleFirstAttemptSubmit}
                        currentScenarioData={currentScenarioData}
                        currentScenario={currentScenario}
                        testId={testId}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {flowStep === 'writing-v2' && renderSecondAttemptWriting()}

      {/* Self Assessment Modal */}
      {showSelfAssessment && flowStep === 'self-assessment' && (
        <SelfAssessmentModal
          rubric={{
            criteria: writingDetail?.rubric_json || [],
            levels: ["B1", "B2", "C1", "C2"],
          }}
          onComplete={handleSelfAssessmentComplete}
          onClose={() => {
            setShowSelfAssessment(false);
            setFlowStep("ai-feedback");
            setShowAIFeedback(true);
          }}
        />
      )}

      {/* AI Feedback - Modified to use API results */}
      {showAIFeedback && flowStep === 'ai-feedback' && (
        <WritingFeedback
          isPlagiarized={isPlagiarized}
          currentScenario={currentScenario}
          handleNextStep={handleAIFeedbackNext}
          handleRetry={null} // No retry in new flow
          writingAttempt={1} // Always show as first attempt
          selfAssessmentData={selfAssessmentData}
          writingMode={writingMode}
          cvData={cvData}
          scenario1Completed={scenario1Completed}
          scenario2Completed={scenario2Completed}
          isFirstAttemptFeedback={true}
          firstAttemptResults={firstAttemptResults}
          currentScenarioData={currentScenarioData}
          apiGradingResults={apiGradingResults} // ✅ NEW: Pass API results
          attemptId={attemptId} // ✅ thêm dòng này
        />
      )}

      {/* Final completion message */}
      {flowStep === 'completed' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-green-800 mb-2">
            Writing Section Completed!
          </h3>
          <p className="text-green-700">
            {currentScenario === 1
              ? "Great job! Moving to Scenario 2..."
              : "Excellent work! Your writing assessment is complete."}
          </p>
        </div>
      )}
    </div>
  );
};

export default WritingSection;